import { useEffect, useState } from 'react';
import type { Order, OrderItem } from '@lr/shared';
import { apiFetch } from '../../lib/api';

type Row = Order & {
  user?: {
    email: string;
    first_name: string | null;
    last_name: string | null;
    shipping_line1?: string | null;
    shipping_line2?: string | null;
    shipping_city?: string | null;
    shipping_postcode?: string | null;
  } | null;
  order_items?: Array<
    OrderItem & { item?: { main_image_url: string | null; name: string; slug: string } | null }
  >;
};

/**
 * Return label requests — orders the customer has flagged as "I want to send
 * this back". Workflow: admin sees the request, prints/emails a label
 * (manual for now), then marks the order 'returned' once it lands back.
 */
export function AdminReturns() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load() {
    setLoading(true);
    apiFetch<Row[]>('/api/admin/return-requests')
      .then(setRows)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  async function markReturned(orderId: string) {
    setUpdating(orderId);
    setError(null);
    try {
      await apiFetch(`/api/admin/orders/${orderId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: 'returned',
          returned_at: new Date().toISOString()
        })
      });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setUpdating(null);
    }
  }

  return (
    <>
      <div className="admin-page-head">
        <div>
          <h1>Return requests</h1>
          <div className="sub">
            Customers who&apos;ve clicked &ldquo;Request return label&rdquo;. Email them
            the label, then mark the order as returned once it&apos;s back.
          </div>
        </div>
      </div>

      {error && <div className="a-alert a-alert-error">{error}</div>}

      <div className="a-card">
        {loading ? (
          <div className="a-empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="a-empty">
            <div className="h">No open return requests</div>
            <div className="p">Once a customer requests a return, it&apos;ll appear here.</div>
          </div>
        ) : (
          <div className="a-table-wrap">
            <table className="a-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Items</th>
                  <th>Shipping address</th>
                  <th style={{ width: 130 }}>Requested</th>
                  <th style={{ width: 160 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.id}>
                    <td className="id">{o.order_number}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        {[o.user?.first_name, o.user?.last_name].filter(Boolean).join(' ') ||
                          '—'}
                      </div>
                      <div className="id">{o.user?.email}</div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {(o.order_items ?? []).map((it) => (
                          <div
                            key={it.id}
                            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                          >
                            <div
                              className="thumb"
                              style={
                                it.item?.main_image_url
                                  ? {
                                      backgroundImage: `url(${it.item.main_image_url})`,
                                      width: 28,
                                      height: 36
                                    }
                                  : { width: 28, height: 36 }
                              }
                            />
                            <span style={{ fontSize: 12 }}>{it.item_name_snapshot}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td style={{ fontSize: 12, lineHeight: 1.4 }}>
                      {o.user?.shipping_line1 ? (
                        <>
                          {o.user.shipping_line1}
                          {o.user.shipping_line2 ? `, ${o.user.shipping_line2}` : ''}
                          <br />
                          {o.user.shipping_city}, {o.user.shipping_postcode}
                        </>
                      ) : (
                        <span style={{ color: 'var(--a-muted)' }}>No address on file</span>
                      )}
                    </td>
                    <td>{new Date(o.updated_at).toLocaleDateString('en-GB')}</td>
                    <td>
                      <button
                        className="btn-primary"
                        disabled={updating === o.id}
                        onClick={() => void markReturned(o.id)}
                      >
                        {updating === o.id ? 'Saving…' : 'Mark returned'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
